import type { CartItem, DbEventRegistration, EntryType, RegistrationWizardState } from '@/types';
import type { EventRegistrationSettings, DayPass } from '@/hooks/config/useEventRegistrationSettings';

export interface EntryState {
  weekendRegistration: DbEventRegistration | null;
  ownedDayPassIds: string[];
}

/**
 * Split a user's registration rows into the (at most one) weekend registration
 * and the day passes they own. Rows are assumed pre-filtered to
 * payment_status = 'completed'.
 */
export function deriveEntryState(rows: DbEventRegistration[]): EntryState {
  return {
    weekendRegistration: rows.find((r) => r.day_pass_id === null) ?? null,
    ownedDayPassIds: rows
      .map((r) => r.day_pass_id)
      .filter((id): id is string => id !== null),
  };
}

/**
 * Tournament selections that remain valid for a chosen entry tier. Day passes
 * are attendance-only — any selection (e.g. a tournament preselected from a
 * tournament page CTA) is dropped when the user picks day passes.
 */
export function tournamentSelectionsForEntry(
  entryType: EntryType | undefined,
  selectedIds: string[],
): string[] {
  return entryType === 'day_pass' ? [] : selectedIds;
}

/** A pass that subsumes others. `covers` names PASS ids, so a bundle can name a bundle. */
function bundleIds(passes: DayPass[]): Set<string> {
  return new Set(passes.filter((p) => (p.covers ?? []).length > 0).map((p) => p.id));
}

/**
 * The day-pass ids already covered by a selected or owned bundle pass (e.g. the
 * weekend pass covers friday/saturday/sunday). The wizard disables these so a user
 * cannot pay for Friday twice.
 *
 * Read from each pass's `covers` field, so no component hardcodes 'weekend'.
 *
 * Coverage is TRANSITIVE: a weekend bundle that covers ['satsun', 'friday'] covers
 * Saturday and Sunday too, because satsun does. Expanding one level would leave those
 * days selectable — and billable — to someone whose bundle already includes them.
 *
 * A pass never covers ITSELF, not even around a cycle, and the visited set means a cyclic
 * hand-edit ('a' covers 'b', 'b' covers 'a') terminates instead of freezing the wizard.
 */
export function coveredDayPassIds(
  passes: DayPass[],
  selectedIds: string[],
  ownedIds: string[] = [],
): string[] {
  const byId = new Map(passes.map((p) => [p.id, p]));
  const covered = new Set<string>();

  for (const rootId of new Set([...selectedIds, ...ownedIds])) {
    const visited = new Set<string>();
    const queue = [rootId];
    while (queue.length > 0) {
      const id = queue.pop() as string;
      if (visited.has(id)) continue;
      visited.add(id);
      for (const coveredId of byId.get(id)?.covers ?? []) {
        if (coveredId === rootId) continue; // a pass never covers itself
        covered.add(coveredId);
        queue.push(coveredId);
      }
    }
  }
  return [...covered];
}

/**
 * Every day of the event, derived from the day-pass config: the leaves (passes that
 * cover nothing, so they ARE a day) UNION every id any bundle names that is not ITSELF
 * a bundle.
 *
 * The union is what makes this survive a hand-edited day_passes row — there is no admin
 * UI, so config drift is routine. Leaves alone are not the day universe:
 *   - Sell ONLY the weekend bundle and there are no leaves at all, so "every leaf" is
 *     vacuously satisfied and a weekend-pass holder would be re-sold the identical $30
 *     spectator tier — the $60 double-charge this whole guard exists to stop.
 *   - Sell Sat/Sun only inside bundles and Friday is the only leaf, so a Friday-only
 *     holder would score as owning the whole weekend and be refused a spectator pass
 *     they were happy to buy.
 * Naming an id in `covers` also keeps a day that no longer has its own pass counted as a
 * day you must hold, so a deleted Sunday pass cannot make Fri+Sat mean "whole weekend".
 *
 * But `covers` names PASS ids, and a pass id can be a BUNDLE ('weekend' covering
 * ['satsun', 'friday']). Counting a bundle as a day makes it a day nobody holds unless
 * they hold that exact bundle — so a buyer with Friday AND Saturday AND Sunday, who
 * plainly has the whole weekend, scores as uncovered and is re-sold the spectator tier.
 * Hence: a bundle is never a day.
 */
function eventDayIds(passes: DayPass[]): Set<string> {
  const bundles = bundleIds(passes);
  const dayIds = new Set<string>();
  for (const pass of passes) {
    const covers = pass.covers ?? [];
    if (covers.length === 0) dayIds.add(pass.id); // a leaf IS a day
    for (const id of covers) {
      // ...and so is anything a bundle names, unless that id is a bundle in its own right
      if (id !== pass.id && !bundles.has(id)) dayIds.add(id);
    }
  }
  return dayIds;
}

/**
 * True when the passes a user already holds grant every day the event sells.
 *
 * This is what stops the $60 double-charge: the spectator entry tier is a full-weekend
 * product at the same price as the weekend day pass, but a weekend day pass records
 * day_pass_id='weekend', so hasWeekendRegistration stays false and nothing else would
 * stop the user buying the same weekend twice.
 *
 * Returns false when NO day passes are configured, so an event without them never has
 * its spectator tier hidden as a side effect. Derived from `covers` rather than the id
 * 'weekend', so a future "Sat+Sun Pass" is handled by the same rule.
 *
 * The server repeats this rule in supabase/functions/_shared/entryItems.ts — the two
 * runtimes cannot share an import. Both are pinned by one coverage table, duplicated in
 * each suite's tests; change one and you must change the other.
 */
export function hasFullWeekendCoverage(passes: DayPass[], ownedIds: string[]): boolean {
  const dayIds = eventDayIds(passes);
  if (dayIds.size === 0) return false; // no day passes configured at all

  const held = new Set([...ownedIds, ...coveredDayPassIds(passes, [], ownedIds)]);
  return [...dayIds].every((id) => held.has(id));
}

/**
 * Toggle one day pass in a selection list. Selecting a bundle pass drops the days it
 * covers, so picking the weekend pass after Friday clears Friday instead of silently
 * billing for both. One-directional on purpose: selecting Friday does not touch the
 * weekend pass, so a user can always upgrade by simply picking the bundle.
 */
export function toggleDayPassSelection(
  selected: string[],
  passId: string,
  passes: DayPass[] = [],
): string[] {
  if (selected.includes(passId)) return selected.filter((id) => id !== passId);
  const covers = new Set(passes.find((p) => p.id === passId)?.covers ?? []);
  return [...selected.filter((id) => !covers.has(id)), passId];
}

/**
 * Build the event-registration cart items for the wizard cart. Weekend tiers
 * produce one item; day passes produce one item per selected day, skipping
 * days already owned and days a selected or owned bundle pass already covers.
 * Returns [] when there is no new entry to charge.
 *
 * The coverage filter mirrors the one in DayPassSelector's displayed total. Both must
 * apply it or a corrupt selection (a stale localStorage cart holding weekend AND friday)
 * would show $30 and bill $45 — filtering here is what makes the two agree.
 */
export function buildEntryCartItems(args: {
  eventRegistration: RegistrationWizardState['eventRegistration'];
  settings: EventRegistrationSettings;
  hasWeekendRegistration: boolean;
  ownedDayPassIds: string[];
}): CartItem[] {
  const { eventRegistration, settings, hasWeekendRegistration, ownedDayPassIds } = args;
  if (!eventRegistration.wantsToPay || hasWeekendRegistration) return [];

  if (eventRegistration.entryType === 'day_pass') {
    const selectedIds =
      eventRegistration.dayPassIds ??
      (eventRegistration.dayPassId ? [eventRegistration.dayPassId] : []);
    const coveredIds = coveredDayPassIds(settings.dayPasses, selectedIds, ownedDayPassIds);
    return selectedIds
      .filter((id) => !ownedDayPassIds.includes(id) && !coveredIds.includes(id))
      .map((id) => settings.dayPasses.find((p) => p.id === id))
      .filter((p): p is NonNullable<typeof p> => !!p)
      .map((pass) => ({
        id: `temp-event-${pass.id}`,
        type: 'event_registration' as const,
        itemId: 'event-2026',
        name: `Cactus Cup 2026 - ${pass.name}`,
        description: pass.description || 'Single-day event pass',
        quantity: 1,
        price: pass.fee,
        dayPassId: pass.id,
        entryType: 'day_pass' as const,
      }));
  }

  const isSpectator = eventRegistration.entryType === 'spectator';
  let entryName: string;
  let entryDescription: string;
  let entryPrice: number;
  if (isSpectator) {
    entryName = settings.spectatorPassName;
    entryDescription = settings.spectatorPassDescription;
    entryPrice = eventRegistration.entryFee ?? settings.spectatorPassFee;
  } else {
    entryName = `${settings.eventRegistrationName}${settings.isEarlyBirdActive ? ' (Early Bird)' : ''}`;
    entryDescription = settings.eventRegistrationDescription;
    entryPrice = eventRegistration.entryFee ?? settings.activeEventRegistrationFee;
  }

  return [
    {
      id: 'temp-event-entry',
      type: 'event_registration' as const,
      itemId: 'event-2026',
      name: `Cactus Cup 2026 - ${entryName}`,
      description: entryDescription,
      quantity: 1,
      price: entryPrice,
      entryType: isSpectator ? ('spectator' as const) : ('paid' as const),
    },
  ];
}

/**
 * The order_items.variant_data payload for one cart item. Entry items carry the day
 * (for day passes) and the chosen tier, which stripe-webhook reads back to persist
 * event_registrations.entry_tier. Other item types keep their variant payload.
 */
export function buildOrderItemVariantData(item: CartItem): Record<string, unknown> | null {
  if (item.type === 'event_registration') {
    return {
      ...(item.dayPassId ? { dayPassId: item.dayPassId } : {}),
      ...(item.entryType ? { entryType: item.entryType } : {}),
    };
  }
  return item.variant?.data || null;
}
